import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";
import type {
  DispatchCenter,
  DispatchRunSchedule,
  ServiceArea,
  Address,
} from "@/api.types.v2";
import type { Carrier } from "@/api/carriers";

const API_URL = import.meta.env.VITE_API_URL;

const CodeConflictSchema = z.object({
  error: z.literal("DUPLICATE_CODE"),
  details: z.object({ field: z.literal("code"), message: z.string().min(1) }),
});

export function getDispatchCenterCodeConflict(error: unknown) {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return;
  const result = CodeConflictSchema.safeParse(error.response.data);
  return result.success ? result.data.details.message : undefined;
}

export type DispatchCenterListItem = DispatchCenter & {
  pickupProviderCarrier: Pick<Carrier, "id" | "code" | "name">;
  serviceArea: Pick<ServiceArea, "id" | "code" | "name">;
  address?: Address;
};

export interface DispatchCenterAddressInput {
  line1: string;
  line2?: string | null;
  city: string;
  province: string;
  country: string;
  postalCode?: string | null;
  location?: unknown;
  plusCode?: string | null;
}

export interface CreateDispatchCenterInput {
  code: string;
  name: string;
  operatorCarrierId: string;
  addressId?: string;
  address?: DispatchCenterAddressInput;
  serviceAreaId: string;
  postalCodes: string[];
  operatingHours?: Record<string, unknown> | null;
  isActive?: boolean;
}

export interface UpdateDispatchCenterInput {
  address?: Partial<DispatchCenterAddressInput>;
  name?: string;
  postalCodes?: string[];
  operatingHours?: Record<string, unknown> | null;
  isActive?: boolean;
  /** Delivery estimates: slot end → ready-at-dispatch minutes. */
  processingMinutes?: number;
  /** Delivery estimates: internal run schedule. null = clear (disables estimates). */
  runSchedule?: DispatchRunSchedule | null;
}

export function useGetDispatchCenters({
  includeInactive = false,
}: {
  includeInactive?: boolean;
} = {}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["dispatch-centers", { includeInactive }],
    queryFn: async () => {
      const { data } = await axios.get<{
        success: boolean;
        data: DispatchCenterListItem[];
      }>(`${API_URL}/api/v2/admin/dispatch-centers`, {
        params: { includeInactive },
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    enabled: !!token,
    staleTime: 60_000,
  });
}

export function useCreateDispatchCenter(options?: {
  onSuccess?: (data: DispatchCenterListItem) => void;
  onError?: (error: Error, input: CreateDispatchCenterInput) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateDispatchCenterInput) => {
      const { data } = await axios.post<{
        success: boolean;
        data: DispatchCenterListItem;
      }>(`${API_URL}/api/v2/admin/dispatch-centers`, input, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["dispatch-centers"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateDispatchCenter(options?: {
  onSuccess?: (data: DispatchCenterListItem) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdateDispatchCenterInput;
    }) => {
      const { data } = await axios.patch<{
        success: boolean;
        data: DispatchCenterListItem;
      }>(`${API_URL}/api/v2/admin/dispatch-centers/${id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["dispatch-centers"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteDispatchCenter(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_URL}/api/v2/admin/dispatch-centers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dispatch-centers"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}
